import { useCallback, useEffect, useState } from 'react';
import { useDebounced } from '@client/new/hooks/useDebounced';
import { CommunityId } from '../model/community.types';
import { FeedTab, MIN_SEARCH_LENGTH } from '../model/feedFilter.types';
import { analytics } from './analytics';

const FEED_DEBOUNCE_MS = 300;
const TRACKING_SETTLE_MS = 1000;

type UseCommunityFilters = {
  tab: FeedTab;
  changeTab: (next: FeedTab) => void;
  search: string;
  setSearch: (value: string) => void;
  debouncedSearch: string;
};

export const useCommunityFilters = (
  activeId: CommunityId | null,
): UseCommunityFilters => {
  const [tab, setTab] = useState<FeedTab>('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, FEED_DEBOUNCE_MS);
  const settledSearch = useDebounced(search, TRACKING_SETTLE_MS);

  useEffect(() => {
    const term = settledSearch.trim();
    if (term.length < MIN_SEARCH_LENGTH || activeId === null) return;
    analytics.trackSearchApplied(activeId, term);
  }, [settledSearch, activeId]);

  const changeTab = useCallback(
    (next: FeedTab) => {
      const reportable = next === 'all' || next === 'mine';
      if (next !== tab && activeId !== null && reportable) {
        analytics.trackFilterApplied(activeId, next);
      }
      setTab(next);
    },
    [tab, activeId],
  );

  return { tab, changeTab, search, setSearch, debouncedSearch };
};
